<template>
  <div class="contact-page">
    <!-- Hero Section -->
    <div class="contact-hero">
      <div class="hero-content">
        <h1>{{ $t('intouch') }}</h1>
        <p>{{ $t('intouchinfo') }}</p>
      </div>
    </div>

    <!-- Main Contact Section -->
    <div class="contact-container">
      <!-- Contact Information Card -->
      <div class="contact-card">
        <div class="contact-info">
          <div class="contact-header">
            <h2 class="section-title">{{ $t('contactinfo') }}</h2>
            <p class="section-subtitle">{{ $t('instructform') }}</p>
          </div>
          <div class="info-grid">
            <div class="info-card">
              <div class="card-icon bg-primary"><i class="fas fa-phone-alt"></i></div>
              <div class="card-content">
                <h3 class="card-title">{{ $t('phone') }}</h3>
                <div class="card-details">
                  <p class="detail-item"><span class="detail-label">{{ $t('Main') }}:</span><span class="detail-value">+237 694 405 868</span></p>
                  <p class="detail-item"><span class="detail-label">{{ $t('emergency') }}:</span><span class="detail-value">8202</span></p>
                </div>
              </div>
            </div>
            <div class="info-card">
              <div class="card-icon bg-accent"><i class="fas fa-envelope"></i></div>
              <div class="card-content">
                <h3 class="card-title">Email</h3>
                <div class="card-details"><p class="detail-item single-line"><span class="detail-value">alerts@cirt.cm</span></p></div>
              </div>
            </div>
            <div class="info-card">
              <div class="card-icon bg-secondary"><i class="fas fa-map-marker-alt"></i></div>
              <div class="card-content">
                <h3 class="card-title">{{ $t('location') }}</h3>
                <div class="card-details">
                  <p class="detail-item">{{ $t('actuallocation') }}</p>
                  <p class="detail-item">{{ $t('townnation') }}</p>
                </div>
              </div>
            </div>
          </div>
          <!-- Social Media Section -->
          <div class="social-section">
            <h3 class="social-title">{{ $t('follow') }}</h3>
            <div class="social-grid">
              <a href="https://www.facebook.com/ANTIC237" target="_blank" rel="noopener" class="social-button facebook"><i class="fab fa-facebook-f"></i><span>Facebook</span></a>
              <a href="https://x.com/antic_cameroun" target="_blank" rel="noopener" class="social-button twitter"><i class="fab fa-twitter"></i><span>Twitter</span></a>
              <a href="https://www.linkedin.com/company/antic/" target="_blank" rel="noopener" class="social-button linkedin"><i class="fab fa-linkedin-in"></i><span>LinkedIn</span></a>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Contact Form -->
      <div class="contact-form-container">
        <div class="form-wrapper">
          <h2>{{ $t('intouch') }}</h2>
          <form @submit.prevent="submitForm" class="contact-form" novalidate>
            <div class="form-group">
              <label for="name">{{ $t('name') }} <span class="required">*</span></label>
              <input type="text" id="name" v-model="form.name" :placeholder="$t('enterName')" :class="{ 'input-error': errors.name }"/>
              <span v-if="errors.name" class="error-text">{{ errors.name }}</span>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="phone">{{ $t('phone') }} <span class="required">*</span></label>
                <input type="tel" id="phone" v-model="form.phone" :placeholder="$t('enterPhone')" :class="{ 'input-error': errors.phone }"/>
                <span v-if="errors.phone" class="error-text">{{ errors.phone }}</span>
              </div>
              <div class="form-group">
                <label for="email">{{ $t('email') }} <span class="required">*</span></label>
                <input type="email" id="email" v-model="form.email" :placeholder="$t('enterEmail')" :class="{ 'input-error': errors.email }"/>
                <span v-if="errors.email" class="error-text">{{ errors.email }}</span>
              </div>
            </div>
            <div class="form-group">
              <label for="description">{{ $t('umsg') }} <span class="required">*</span></label>
              <textarea id="description" v-model="form.description" rows="5" :placeholder="$t('description')" :class="{ 'input-error': errors.description }"></textarea>
              <span v-if="errors.description" class="error-text">{{ errors.description }}</span>
            </div>

            <!-- reCAPTCHA Container -->
            <div ref="recaptchaContainer" class="g-recaptcha-wrapper"></div>
            <span v-if="errors.recaptcha" class="error-text recaptcha-error">{{ errors.recaptcha }}</span>
            
            <button type="submit" class="submit-btn" :disabled="loading || !recaptchaVerified">
              <span v-if="!loading">{{ $t('submit') }}</span>
              <span v-else class="loading-spinner"><i class="fas fa-spinner fa-spin"></i> {{ $t('submiting') }}</span>
            </button>
            
            <div v-if="successMessage" class="success-message"><i class="fas fa-check-circle"></i> {{ successMessage }}</div>
            <div v-if="errorMessage" class="error-display"><i class="fas fa-exclamation-circle"></i> {{ errorMessage }}</div>
          </form>
        </div>
      </div>
    </div>
    
    <!-- Map Section -->
    <div class="map-container">
      <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3979.805317015862!2d11.50627731576125!3d3.874022397064597!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zM8KwNTInMjYuNSJOIDExwrAzMCcyMi42IkU!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus" width="100%" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade" title="Location Map: ANTIC, VGX6+H92, 1630, Yaoundé"></iframe>
      <button class="directions-button" @click="getDirections"><i class="fas fa-route"></i> {{ $t('getDirections') }}</button>
    </div>
    
  </div>
</template>

<script>
import axios from 'axios';
const BaseURL = process.env.VUE_APP_API_BASE_URL;

export default {
  name: 'ContactPage',

  data() {
    return {
      form: { name: '', phone: '', email: '', description: '' },
      errors: {},
      loading: false,
      successMessage: '',
      errorMessage: '',
      isMounted: false,
      recaptchaWidgetId: null,
      recaptchaSiteKey: '6LeRuzQrAAAAAKT8aj-Z7dCoHVs6SudaDsPJHnB0',
      recaptchaVerified: false,
      recaptchaToken: '',
      recaptchaPollTimer: null
    };
  },

  mounted() {
    this.isMounted = true;
    this.$nextTick(() => {
      this.initRecaptcha();
    });
  },

  beforeUnmount() {
    this.isMounted = false;
    if (this.recaptchaPollTimer) {
      clearInterval(this.recaptchaPollTimer);
      this.recaptchaPollTimer = null;
    }
    this.recaptchaWidgetId = null;
  },

  methods: {
    /* --------------------------------------------------------
       reCAPTCHA v2 — explicit render with grecaptcha.ready()
    -------------------------------------------------------- */
    initRecaptcha() {
      // Step 1: Ensure the script tag exists in <head>
      if (!document.querySelector('script[src*="recaptcha/api.js"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }

      // Step 2: Wait until grecaptcha is fully ready, then render.
      // grecaptcha.ready() is Google's official way — it queues the
      // callback and fires it as soon as the API is initialized, even
      // if the script hasn't finished loading yet.
      this.waitForGrecaptcha();
    },

    waitForGrecaptcha() {
      // If grecaptcha.ready exists, use it (most reliable)
      if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
        window.grecaptcha.ready(() => {
          if (this.isMounted) this.renderRecaptcha();
        });
        return;
      }

      // Otherwise poll until it becomes available
      let attempts = 0;
      this.recaptchaPollTimer = setInterval(() => {
        attempts++;
        if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
          clearInterval(this.recaptchaPollTimer);
          this.recaptchaPollTimer = null;
          window.grecaptcha.ready(() => {
            if (this.isMounted) this.renderRecaptcha();
          });
        } else if (attempts >= 100) {
          // 100 × 100ms = 10s timeout
          clearInterval(this.recaptchaPollTimer);
          this.recaptchaPollTimer = null;
}
      }, 100);
    },

    renderRecaptcha() {
      const container = this.$refs.recaptchaContainer;
      if (!container) {
return;
      }
      if (this.recaptchaWidgetId !== null) return;

      // Clear any stale HTML from previous mount
      container.innerHTML = '';

      try {
        this.recaptchaWidgetId = window.grecaptcha.render(container, {
          sitekey: this.recaptchaSiteKey,
          callback: (token) => { this.onCaptchaVerified(token); },
          'expired-callback': () => { this.onCaptchaExpired(); },
          'error-callback': () => { this.onCaptchaError(); }
        });
} catch (e) {
      // silent
      }
    },

    onCaptchaVerified(token) {
      this.recaptchaVerified = true;
      this.recaptchaToken = token;
      this.errors.recaptcha = '';
    },

    onCaptchaExpired() {
      this.recaptchaVerified = false;
      this.recaptchaToken = '';
      if (this.isMounted) this.errors.recaptcha = this.$t('validation.captchaExpired');
    },

    onCaptchaError() {
      this.recaptchaVerified = false;
      this.recaptchaToken = '';
      if (this.isMounted) this.errors.recaptcha = this.$t('validation.captchaFailed');
    },

    /* --------------------------------------------------------
       Validation
    -------------------------------------------------------- */
    validateForm() {
      this.errors = {};
      let valid = true;

      if (!this.form.name.trim()) { this.errors.name = this.$t('validation.nameRequired'); valid = false; }
      if (!this.form.phone.trim()) { this.errors.phone = this.$t('validation.phoneRequired'); valid = false; }
      else if (!/^[\d\s+()-]+$/.test(this.form.phone)) { this.errors.phone = this.$t('validation.phoneInvalid'); valid = false; }
      if (!this.form.email.trim()) { this.errors.email = this.$t('validation.emailRequired'); valid = false; }
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) { this.errors.email = this.$t('validation.emailInvalid'); valid = false; }
      if (!this.form.description.trim()) { this.errors.description = this.$t('validation.descriptionRequired'); valid = false; }
      if (!this.recaptchaVerified) { this.errors.recaptcha = this.$t('validation.captchaRequired'); valid = false; }

      return valid;
    },

    /* --------------------------------------------------------
       Submit
    -------------------------------------------------------- */
    async submitForm() {
      if (!this.validateForm()) return;

      this.loading = true;
      this.successMessage = '';
      this.errorMessage = '';

      try {
        const formData = new FormData();
        formData.append('name', this.form.name);
        formData.append('phone', this.form.phone);
        formData.append('email', this.form.email);
        formData.append('description', this.form.description);
        formData.append('recaptcha_token', this.recaptchaToken);

        const response = await axios.post(`${BaseURL}/incident/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.status === 200 || response.status === 201) {
          this.successMessage = this.$t('success');
          this.resetForm();
          this.resetRecaptcha();
        } else {
          this.errorMessage = this.$t('error.submitFailed');
        }
      } catch (error) {
        if (error.response) {
          if (error.response.status === 400) {
            const data = error.response.data;
            if (data.error) {
              this.errorMessage = data.error;
            } else {
              for (const field in data) {
                if (Array.isArray(data[field])) this.errors[field] = data[field].join(' ');
              }
              this.errorMessage = this.$t('error.validationFailed');
            }
          } else {
            this.errorMessage = error.response.data?.error || this.$t('error.serverError');
          }
        } else {
          this.errorMessage = this.$t('error.networkError');
        }
      } finally {
        this.loading = false;
        setTimeout(() => { this.successMessage = ''; this.errorMessage = ''; }, 6000);
      }
    },

    /* --------------------------------------------------------
       Helpers
    -------------------------------------------------------- */
    resetForm() {
      this.form = { name: '', phone: '', email: '', description: '' };
      this.errors = {};
    },

    resetRecaptcha() {
      this.recaptchaVerified = false;
      this.recaptchaToken = '';
      if (window.grecaptcha && this.recaptchaWidgetId !== null) {
        window.grecaptcha.reset(this.recaptchaWidgetId);
      }
    },

    getDirections() {
      window.open(
        'https://www.google.com/maps/dir/?api=1&destination=3.874022397064597,11.50627731576125',
        '_blank',
        'noopener,noreferrer'
      );
    }
  }
};
</script>

<style scoped>
.contact-page { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; line-height: 1.6; }
.contact-hero { background: #faf7f2; color: #1E293B; padding: 60px 20px; text-align: center; }
.hero-content h1 { font-size: 2.5rem; margin-bottom: 20px; font-weight: 700; color: #144A78; }
.hero-content p { font-size: 1.2rem; color: #64748B; max-width: 800px; margin: 0 auto; }
.contact-container { max-width: 1200px; margin: 40px auto; padding: 0 20px; display: grid; grid-template-columns: 1fr 1.5fr; gap: 40px; }
.contact-card { background: white; border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); overflow: hidden; }
.contact-header { padding: 2rem 2rem 1rem; text-align: center; border-bottom: 1px solid #E2E8F0; }
.section-title { font-size: 1.5rem; font-weight: 600; color: #144A78; margin-bottom: 0.5rem; }
.section-subtitle { color: #64748B; font-size: 0.9rem; }
.info-grid { display: grid; grid-template-columns: 1fr; gap: 1rem; padding: 2rem; }
.info-card { display: flex; align-items: center; gap: 1.25rem; background: #F8FAFC; border-radius: 10px; padding: 1.5rem; }
.card-icon { width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 1.2rem; flex-shrink: 0; }
.bg-primary { background: #144A78; }
.bg-accent { background: #E53E3E; }
.bg-secondary { background: #2D3748; }
.card-title { font-size: 1.1rem; font-weight: 600; color: #1E293B; margin-bottom: 0.5rem; }
.card-details { display: flex; flex-direction: column; gap: 0.5rem; font-size: 0.95rem; color: #475569; }
.detail-label { font-weight: 500; color: #334155; margin-right: 0.5rem; }
.social-section { padding: 2rem; text-align: center; border-top: 1px solid #E2E8F0; }
.social-title { font-size: 1.1rem; color: #144A78; margin-bottom: 1.25rem; }
.social-grid { display: flex; justify-content: center; gap: 1rem; flex-wrap: wrap; }
.social-button { display: inline-flex; align-items: center; gap: 0.5rem; padding: 0.6rem 1.2rem; border-radius: 50px; font-size: 0.9rem; font-weight: 500; color: white; text-decoration: none; transition: all 0.2s ease; }
.social-button:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
.facebook { background: #1877F2; }
.twitter { background: #1DA1F2; }
.linkedin { background: #0A66C2; }
.map-container { position: relative; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.1); max-width: 1200px; margin: 40px auto; padding: 0 20px; }
.directions-button { position: absolute; bottom: 20px; left: 50%; transform: translateX(-50%); background: #144A78; color: white; border: none; padding: 12px 24px; border-radius: 50px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.2); transition: all 0.3s ease; }
.directions-button:hover { background: #0D3A5F; transform: translateX(-50%) scale(1.05); }

/* ---- Form styles ---- */
.contact-form-container { background: white; border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); padding: 40px; }
.form-wrapper h2 { font-size: 1.8rem; margin-bottom: 25px; color: #1E293B; text-align: center; font-weight: 700; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.form-group { margin-bottom: 20px; }
.form-group label { display: block; margin-bottom: 8px; font-weight: 600; color: #334155; font-size: 0.9rem; }
.required { color: #e74c3c; }
.form-group input,
.form-group textarea { width: 100%; padding: 12px 15px; border: 1.5px solid #d1d5db; border-radius: 8px; font-size: 0.95rem; transition: all 0.2s ease; background-color: #ffffff; color: #1E293B; }
.form-group input::placeholder,
.form-group textarea::placeholder { color: #9ca3af; }
.form-group input:focus,
.form-group textarea:focus { outline: none; border-color: #144A78; box-shadow: 0 0 0 3px rgba(20, 74, 120, 0.12); }
.form-group textarea { resize: vertical; font-family: inherit; }
.input-error { border-color: #e74c3c !important; background-color: #fef2f2 !important; }
.error-text { color: #e74c3c; font-size: 0.82rem; margin-top: 5px; display: block; }
.recaptcha-error { text-align: center; }

/* ---- reCAPTCHA ---- */
.g-recaptcha-wrapper { margin: 20px 0; min-height: 78px; display: flex; justify-content: center; align-items: center; background: #f8fafc; border: 1px dashed #d1d5db; border-radius: 8px; padding: 12px; }

/* ---- Submit Button ---- */
.submit-btn { background: #144A78; color: white; border: none; padding: 14px 25px; border-radius: 8px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: all 0.2s ease; width: 100%; }
.submit-btn:hover:not(:disabled) { background: #0D3A5F; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(20, 74, 120, 0.3); }
.submit-btn:disabled { background: #94a3b8; cursor: not-allowed; }
.loading-spinner { display: flex; align-items: center; justify-content: center; gap: 8px; }

/* ---- Feedback Messages ---- */
.success-message, .error-display { text-align: center; margin-top: 20px; padding: 12px 15px; border-radius: 8px; font-size: 0.95rem; display: flex; align-items: center; justify-content: center; gap: 8px; }
.success-message { background: #ecfdf5; color: #166534; border: 1px solid #bbf7d0; }
.error-display { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }

@media (max-width: 992px) { .contact-container { grid-template-columns: 1fr; } }
@media (max-width: 768px) { .form-row { grid-template-columns: 1fr; gap: 0; } }
</style>